/**
 * Analytics — what the ledger can honestly tell you, and nothing it cannot.
 *
 * FOUR THINGS, each earning its form (the reasoning, so it is not "improved"
 * into a wall of charts later):
 *
 *  1. A KPI row — money in, money out, net, savings rate. Four headline
 *     numbers are a row of stat tiles, never a grouped bar chart of four bars.
 *  2. A line chart of in vs out over time. Both series are money, so they
 *     share ONE axis; a second y-scale would invent a correlation that is not
 *     in the data. Money out is the subject (accent), money in the context
 *     (muted) — emphasis, not two competing hues.
 *  3. A donut of spending by bucket. Three slices of a whole that means
 *     something is the one job a donut is actually good at. It is NOT for
 *     comparing close values — the table below carries the numbers.
 *  4. A ranked table of categories. Past a handful of classes a table beats
 *     more colours, and it doubles as the table view every chart owes.
 *
 * Deliberately NOT here: a chart per category (a 9th hue is indistinguishable
 * from an existing one), a pie of two slices, a number on every data point.
 *
 * CHARTS ARE HAND-ROLLED SVG, no charting dependency. Not stubbornness: the
 * bucket colours are user-configurable CSS variables (AGENTS.md §3.5h), and
 * inline SVG that says `fill="var(--needs)"` follows a colour change and a
 * dark-mode flip for free, where a charting library would need both threaded
 * through its own theme object.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Analytics as AnalyticsData, Category } from '@et/contracts';
import { BUCKET_LABELS, SPENDING_BUCKETS, type Bucket } from '@et/core';
import { api, messageOf, type Identity } from './api.js';

export interface AnalyticsFilters {
  from: string;
  to: string;
  bucket: string;
  categoryIds: string[];
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Ranges people actually ask for, rather than a date picker and good luck. */
const PRESETS: Array<{ id: string; label: string; of: () => { from: string; to: string } }> = [
  {
    id: 'this-month',
    label: 'This month',
    of: () => {
      const now = new Date();
      return {
        from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))),
        to: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0))),
      };
    },
  },
  {
    id: 'last-month',
    label: 'Last month',
    of: () => {
      const now = new Date();
      return {
        from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))),
        to: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0))),
      };
    },
  },
  {
    id: 'last-3',
    label: 'Last 3 months',
    of: () => {
      const now = new Date();
      return {
        from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 2, 1))),
        to: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0))),
      };
    },
  },
  {
    id: 'last-12',
    label: 'Last 12 months',
    of: () => {
      const now = new Date();
      return {
        from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1))),
        to: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0))),
      };
    },
  },
  {
    id: 'this-year',
    label: 'This year',
    of: () => {
      const year = new Date().getUTCFullYear();
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    },
  },
];

/** This month, by name rather than by index — a reordered list must not
 *  silently change what the screen opens on. */
export const DEFAULT_ANALYTICS_FILTERS: AnalyticsFilters = {
  ...PRESETS.find((p) => p.id === 'this-month')!.of(),
  bucket: '',
  categoryIds: [],
};

const DAYS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "1 Jul – 30 Sep 2026", with the year said once when both ends share it. */
function rangeLabel(from: string, to: string): string {
  const [fy, fm, fd] = from.split('-');
  const [ty, tm, td] = to.split('-');
  const start = `${Number(fd)} ${DAYS[Number(fm) - 1]}${fy === ty ? '' : ` ${fy}`}`;
  return `${start} – ${Number(td)} ${DAYS[Number(tm) - 1]} ${ty}`;
}

interface Props {
  identity: Identity;
  filters: AnalyticsFilters;
}

export function Analytics({ identity, filters }: Props) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await api.analytics(identity, {
        from: filters.from,
        to: filters.to,
        ...(filters.bucket ? { bucket: filters.bucket as 'NEEDS' | 'WANTS' | 'SAVINGS' } : {}),
        ...(filters.categoryIds.length > 0 ? { categoryIds: filters.categoryIds.join(',') } : {}),
      });
      setData(next);
      setError(null);
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setLoading(false);
    }
  }, [identity, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const narrowed = Boolean(filters.bucket) || filters.categoryIds.length > 0;

  return (
    <section className="analytics">
      {error && <p className="error">{error}</p>}

      {/* Held at reduced opacity while refetching rather than swapped for a
          skeleton: a skeleton flash on every filter change is a layout jump
          three times a second. */}
      <div className={loading && data ? 'analytics-body is-stale' : 'analytics-body'}>
        {!data && loading && <p className="hint">Loading…</p>}

        {data && (
          <>
            <Kpis data={data} narrowed={narrowed} />

            {data.series.length === 0 || isEmpty(data) ? (
              <p className="hint analytics-empty">
                Nothing recorded in this range. Record an expense or some income and the numbers
                appear here — this screen reads the ledger, it has nothing of its own.
              </p>
            ) : (
              <>
                <TrendChart data={data} narrowed={narrowed} />
                <div className="analytics-split">
                  <BucketDonut data={data} />
                  <CategoryTable data={data} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function isEmpty(data: AnalyticsData): boolean {
  return num(data.totals.income) === 0 && num(data.totals.spending) === 0;
}

/** Decimal string → number. Display only; never fed back into an amount. */
function num(decimal: string): number {
  return Number(decimal);
}

function money(decimal: string, currency: string): string {
  return `${decimal} ${currency}`;
}

// ---------------------------------------------------------------------------
// Filters — one row, above everything it scopes, and rendered by App into the
// page header rather than by this screen.
//
// The header is the one band that is already sticky, so the range stays
// reachable after scrolling past the charts instead of needing a second pinned
// strip of its own. It is also the same place the journal's filters and the
// month picker live, so there is one filter position in this app rather than
// three that merely resemble each other (AGENTS.md §3.5e).
//
// It stays ONE row above EVERYTHING it scopes — never a control inside a chart
// card, which would let two charts disagree about which slice they are drawing.
// ---------------------------------------------------------------------------

export function AnalyticsFilterBar({
  categories,
  filters,
  onFiltersChange,
}: {
  categories: Category[];
  filters: AnalyticsFilters;
  onFiltersChange: (next: AnalyticsFilters) => void;
}) {
  /**
   * One panel open at a time. Two popovers that can both be open overlap each
   * other on a narrow header and there is no arrangement that fixes it.
   */
  const [panel, setPanel] = useState<'range' | 'categories' | null>(null);
  const anchor = useRef<HTMLDivElement | null>(null);

  const spending = useMemo(
    () => categories.filter((c) => c.bucket !== 'INCOME' && !c.archived).sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );

  const activePreset = PRESETS.find((p) => {
    const range = p.of();
    return range.from === filters.from && range.to === filters.to;
  });

  useEffect(() => {
    if (!panel) return;
    function onPointerDown(event: PointerEvent) {
      if (!anchor.current?.contains(event.target as Node)) setPanel(null);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setPanel(null);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [panel]);

  function set(patch: Partial<AnalyticsFilters>) {
    onFiltersChange({ ...filters, ...patch });
  }

  function toggleCategory(id: string) {
    const next = filters.categoryIds.includes(id)
      ? filters.categoryIds.filter((c) => c !== id)
      : [...filters.categoryIds, id];
    set({ categoryIds: next });
  }

  return (
    <div className="analytics-filters" ref={anchor}>
      <div className="analytics-facets">
        <select
          value={filters.bucket}
          onChange={(e) => set({ bucket: e.target.value })}
          aria-label="Bucket"
        >
          <option value="">Every bucket</option>
          {SPENDING_BUCKETS.map((b) => (
            <option key={b} value={b}>
              {BUCKET_LABELS[b]}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={filters.categoryIds.length > 0 ? 'chip chip-on' : 'chip chip-ghost'}
          onClick={() => setPanel((p) => (p === 'categories' ? null : 'categories'))}
          aria-expanded={panel === 'categories'}
        >
          {filters.categoryIds.length > 0
            ? `${filters.categoryIds.length} ${filters.categoryIds.length === 1 ? 'category' : 'categories'}`
            : 'Categories'}
        </button>

        {(filters.bucket || filters.categoryIds.length > 0) && (
          <button
            type="button"
            className="chip chip-ghost"
            onClick={() => set({ bucket: '', categoryIds: [] })}
          >
            Clear
          </button>
        )}

        {panel === 'categories' && (
          <div className="popover analytics-popover" role="dialog" aria-label="Filter by category">
            {spending.length === 0 && <p className="hint">No categories yet.</p>}
            {spending.map((c) => (
              <label key={c.id} className="popover-check">
                <input
                  type="checkbox"
                  checked={filters.categoryIds.includes(c.id)}
                  onChange={() => toggleCategory(c.id)}
                />
                <span
                  className={c.color ? 'dot' : `dot dot-${c.bucket.toLowerCase()}`}
                  style={c.color ? { background: c.color } : undefined}
                  aria-hidden="true"
                />
                {c.name}
              </label>
            ))}
          </div>
        )}
      </div>

      {/*
        ONE control for the range, relative and absolute together.
        A row of preset chips beside a pair of date inputs was two controls for
        one decision, and it left the header saying four things at once while
        answering none of them: you could not tell at a glance WHICH range you
        were looking at. The button now states the answer — the preset's name
        while one matches, the dates themselves once you have typed your own —
        and everything you might change it to lives behind it.

        LAST in the row, and last in the DOM, so the visual order and the tab
        order are the same thing. Pushing it right with `order` instead would
        have left a keyboard user reaching it before the controls drawn to its
        left, which is the usual way a tidy-looking row becomes unusable.
      */}
      <div className="range-control">
        <button
          type="button"
          className="chip chip-range"
          onClick={() => setPanel((p) => (p === 'range' ? null : 'range'))}
          aria-expanded={panel === 'range'}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="3.2" y="4.8" width="17.6" height="16" rx="2.4" />
            <path d="M3.2 9.6h17.6M8.4 3.2v3.2M15.6 3.2v3.2" strokeLinecap="round" />
          </svg>
          {activePreset ? activePreset.label : rangeLabel(filters.from, filters.to)}
          <span className="chip-caret" aria-hidden="true">▾</span>
        </button>

        {panel === 'range' && (
          <div className="popover range-pop" role="dialog" aria-label="Date range">
            <div className="range-pop-presets">
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={activePreset?.id === preset.id ? 'range-option on' : 'range-option'}
                  aria-pressed={activePreset?.id === preset.id}
                  onClick={() => {
                    set(preset.of());
                    // A preset is a complete answer, so the panel closes on it.
                    // Typing a date is not, so the panel below stays open.
                    setPanel(null);
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="range-pop-custom">
              <span className="field-label">Or pick the days</span>
              <div className="range-custom">
                <label>
                  <span className="sr-only">From</span>
                  <input
                    type="date"
                    value={filters.from}
                    max={filters.to}
                    onChange={(e) => e.target.value && set({ from: e.target.value })}
                    aria-label="From"
                  />
                </label>
                <span className="range-dash" aria-hidden="true">–</span>
                <label>
                  <span className="sr-only">To</span>
                  <input
                    type="date"
                    value={filters.to}
                    min={filters.from}
                    onChange={(e) => e.target.value && set({ to: e.target.value })}
                    aria-label="To"
                  />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The headline numbers. Four numbers are a KPI row, not a chart.
// ---------------------------------------------------------------------------

function Kpis({ data, narrowed }: { data: AnalyticsData; narrowed: boolean }) {
  const { totals, currency } = data;
  const net = num(totals.net);

  return (
    <div className="kpi-row">
      <Kpi
        label="Money in"
        value={money(totals.income, currency)}
        note={narrowed ? 'the whole range — income has no category' : undefined}
      />
      <Kpi label="Money out" value={money(totals.spending, currency)} note={narrowed ? 'filtered' : undefined} />
      <Kpi
        label="Net"
        value={money(totals.net, currency)}
        tone={net < 0 ? 'bad' : net > 0 ? 'good' : undefined}
      />
      <Kpi
        label="Savings rate"
        value={totals.savingsRate === null ? '—' : `${Math.round(totals.savingsRate * 100)}%`}
        note={totals.savingsRate === null ? 'nothing came in' : 'of money in'}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string | undefined;
  tone?: 'good' | 'bad' | undefined;
}) {
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      {/* Proportional figures, not tabular: equal-width digits make a large
          standalone number look loose. Tabular is for the table below. */}
      <strong className={tone ? `kpi-value kpi-${tone}` : 'kpi-value'}>{value}</strong>
      {note && <span className="kpi-note">{note}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// In and out, over time. Two money series, ONE axis.
// ---------------------------------------------------------------------------

/* `left` leaves room for the y-axis tick labels, `bottom` for the x-axis band —
   sizing the box to the plot alone is what gives a card a tiny nested scroll. */
const PLOT = { width: 720, height: 220, left: 54, right: 10, top: 14, bottom: 26 };

function TrendChart({ data, narrowed }: { data: AnalyticsData; narrowed: boolean }) {
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState<number | null>(null);

  const points = data.series;
  const max = Math.max(1, ...points.map((p) => Math.max(num(p.income), num(p.spending))));

  const innerW = PLOT.width - PLOT.left - PLOT.right;
  const innerH = PLOT.height - PLOT.top - PLOT.bottom;
  const x = (i: number) => PLOT.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (value: number) => PLOT.top + innerH - (value / max) * innerH;

  const path = (pick: (p: (typeof points)[number]) => number) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(pick(p)).toFixed(1)}`).join(' ');

  const ticks = [0, max / 2, max];
  const last = points.length - 1;

  /**
   * The one point worth labelling directly: the biggest spend, not the last
   * point. Labelling the endpoint is the usual advice and it was wrong here —
   * a range that ends in a quiet week put "0.00" on the chart, which reads as
   * broken. The extreme is what a reader is looking for anyway. Nothing is
   * labelled at all when nothing was spent.
   */
  const peak = points.reduce(
    (best, p, i) => (num(p.spending) > num(points[best]!.spending) ? i : best),
    0,
  );
  const hasSpend = num(points[peak]!.spending) > 0;

  return (
    <figure className="chart-card">
      <figcaption>
        <div>
          <h3>Money in and out</h3>
          <p className="hint">
            {byPeriod(data.granularity)}
            {narrowed && ' · money out is filtered, money in is the whole range'}
          </p>
        </div>
        <div className="chart-tools">
          {/* A legend is always present for two series — identity never rests
              on colour alone. */}
          <span className="legend">
            <span className="legend-key"><i className="key key-out" />Money out</span>
            <span className="legend-key"><i className="key key-in" />Money in</span>
          </span>
          <button type="button" className="chip chip-ghost" onClick={() => setAsTable((t) => !t)}>
            {asTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </figcaption>

      {asTable ? (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col" className="num">Money in</th>
              <th scope="col" className="num">Money out</th>
              <th scope="col" className="num">Net</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.periodStart}>
                <th scope="row">{p.label}</th>
                <td className="num">{p.income}</td>
                <td className="num">{p.spending}</td>
                <td className="num">{p.net}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="plot-wrap">
          <svg
            viewBox={`0 0 ${PLOT.width} ${PLOT.height}`}
            className="plot"
            role="img"
            aria-label={`Money in and out, ${byPeriod(data.granularity)}. The table view has every value.`}
            onMouseLeave={() => setHover(null)}
          >
            {/* Hairline, solid, one step off the surface. Never dashed. The
                tick labels carry every value that is not directly labelled. */}
            {ticks.map((t, i) => (
              <g key={i}>
                <line x1={PLOT.left} x2={PLOT.width - PLOT.right} y1={y(t)} y2={y(t)} className="grid" />
                <text x={PLOT.left - 8} y={y(t) + 3.5} className="plot-tick" textAnchor="end">
                  {compact(t)}
                </text>
              </g>
            ))}

            {/* A wash, never a saturated block. */}
            <path d={`${path((p) => num(p.spending))} L${x(last)},${y(0)} L${x(0)},${y(0)} Z`} className="area-out" />

            <path d={path((p) => num(p.income))} className="line-in" />
            <path d={path((p) => num(p.spending))} className="line-out" />

            {hover !== null && points[hover] && (
              <>
                <line x1={x(hover)} x2={x(hover)} y1={PLOT.top} y2={PLOT.top + innerH} className="crosshair" />
                <circle cx={x(hover)} cy={y(num(points[hover]!.income))} r="4.5" className="pt-in" />
                <circle cx={x(hover)} cy={y(num(points[hover]!.spending))} r="4.5" className="pt-out" />
              </>
            )}

            {/* ONE direct label — a value on every point is chaos and goes
                unread. Text wears a text token, never the series colour. The
                anchor flips at the edges so the label never runs off the plot. */}
            {hasSpend && (
              <text
                x={x(peak)}
                y={y(num(points[peak]!.spending)) - 9}
                className="plot-label"
                textAnchor={peak === 0 ? 'start' : peak === last ? 'end' : 'middle'}
              >
                {points[peak]!.spending}
              </text>
            )}

            {points.map((p, i) => (
              <rect
                key={p.periodStart}
                x={x(i) - innerW / Math.max(points.length, 1) / 2}
                y={PLOT.top}
                width={innerW / Math.max(points.length, 1)}
                height={innerH}
                className="hit"
                onMouseEnter={() => setHover(i)}
                onFocus={() => setHover(i)}
                tabIndex={0}
                role="button"
                aria-label={`${p.label}: in ${p.income}, out ${p.spending}`}
              />
            ))}

            {points.map((p, i) =>
              showTick(i, points.length) ? (
                <text key={p.periodStart} x={x(i)} y={PLOT.height - 8} className="plot-tick" textAnchor="middle">
                  {p.label}
                </text>
              ) : null,
            )}
          </svg>

          {hover !== null && points[hover] && (
            <div className="plot-tip" style={{ left: `${(x(hover) / PLOT.width) * 100}%` }}>
              <strong>{points[hover]!.label}</strong>
              <span>In {money(points[hover]!.income, data.currency)}</span>
              <span>Out {money(points[hover]!.spending, data.currency)}</span>
              <span>Net {money(points[hover]!.net, data.currency)}</span>
            </div>
          )}
        </div>
      )}
    </figure>
  );
}

function byPeriod(granularity: AnalyticsData['granularity']): string {
  return granularity === 'day' ? 'by day' : granularity === 'week' ? 'by week' : 'by month';
}

/**
 * Thin the x ticks so they never collide; the tooltip and the table carry the
 * rest. The last point earns a tick only when it is far enough from the one
 * before it — forcing it unconditionally printed "25 Sep" and "28 Sep" on top
 * of each other at phone width.
 */
function showTick(index: number, total: number): boolean {
  const step = Math.ceil(total / 7);
  if (index % step === 0) return true;
  if (index !== total - 1) return false;
  return (total - 1) % step >= step / 2;
}

/**
 * A compact y tick: 1.2k rather than 1200.00. The axis carries the values that
 * were not directly labelled, so it has to stay readable at three characters.
 */
function compact(value: number): string {
  if (value >= 1_000_000) return `${round1(value / 1_000_000)}M`;
  if (value >= 1000) return `${round1(value / 1000)}k`;
  return String(Math.round(value));
}

function round1(value: number): string {
  return (Math.round(value * 10) / 10).toString();
}

// ---------------------------------------------------------------------------
// Share of spending by bucket. Three slices of a whole that means something.
// ---------------------------------------------------------------------------

function BucketDonut({ data }: { data: AnalyticsData }) {
  const slices = SPENDING_BUCKETS.map((bucket) => {
    const row = data.byBucket.find((b) => b.bucket === bucket);
    return { bucket, amount: row?.amount ?? '0.00', share: row?.share ?? 0 };
  }).filter((s) => s.share > 0);

  if (slices.length === 0) return null;

  const R = 52;
  const STROKE = 22;
  const circumference = 2 * Math.PI * R;
  // A 2px gap in the surface colour separates touching fills — never a stroke
  // drawn around them.
  const GAP = 2;

  let offset = 0;
  const arcs = slices.map((slice) => {
    const length = Math.max(0, slice.share * circumference - GAP);
    const arc = { ...slice, length, offset };
    offset += slice.share * circumference;
    return arc;
  });

  return (
    <figure className="chart-card">
      <figcaption>
        <div>
          <h3>Where it went</h3>
          <p className="hint">Share of money out, by bucket</p>
        </div>
      </figcaption>

      <div className="donut-wrap">
        <svg viewBox="0 0 130 130" className="donut" role="img" aria-label="Share of money out by bucket">
          <g transform="rotate(-90 65 65)">
            {arcs.map((arc) => (
              <circle
                key={arc.bucket}
                cx="65"
                cy="65"
                r={R}
                fill="none"
                stroke={`var(--${arc.bucket.toLowerCase()})`}
                strokeWidth={STROKE}
                strokeDasharray={`${arc.length} ${circumference - arc.length}`}
                strokeDashoffset={-arc.offset}
              />
            ))}
          </g>
        </svg>

        {/* The legend is the label. Text wears text tokens; the swatch beside
            it carries identity. */}
        <ul className="donut-legend">
          {slices.map((slice) => (
            <li key={slice.bucket}>
              <span className={`dot dot-${slice.bucket.toLowerCase()}`} aria-hidden="true" />
              <span className="donut-name">{BUCKET_LABELS[slice.bucket as Bucket]}</span>
              <span className="donut-share">{Math.round(slice.share * 100)}%</span>
              <span className="donut-amount">{slice.amount}</span>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// The categories, ranked. Past a handful of classes a table beats more colours.
// ---------------------------------------------------------------------------

function CategoryTable({ data }: { data: AnalyticsData }) {
  if (data.byCategory.length === 0) return null;

  return (
    <figure className="chart-card">
      <figcaption>
        <div>
          <h3>By category</h3>
          <p className="hint">Money out in this range, largest first</p>
        </div>
      </figcaption>

      <table className="data-table cat-spend">
        <thead>
          <tr>
            <th scope="col">Category</th>
            <th scope="col" className="num">Spent</th>
            <th scope="col" className="num">Share</th>
            <th scope="col" className="num">Entries</th>
          </tr>
        </thead>
        <tbody>
          {data.byCategory.map((row) => (
            <tr key={row.categoryId}>
              <th scope="row">
                <span
                  className={row.color ? 'dot' : `dot dot-${row.bucket.toLowerCase()}`}
                  style={row.color ? { background: row.color } : undefined}
                  aria-hidden="true"
                />
                {row.name}
              </th>
              <td className="num">{row.amount}</td>
              <td className="num share-cell">
                {/* The bar is the same information as the number beside it,
                    which is the point: it makes the ranking scannable without
                    asking anyone to read four decimals. */}
                <span className="share-bar" aria-hidden="true">
                  <i
                    style={{
                      width: `${Math.max(2, row.share * 100)}%`,
                      background: row.color ?? `var(--${row.bucket.toLowerCase()})`,
                    }}
                  />
                </span>
                {Math.round(row.share * 100)}%
              </td>
              <td className="num">{row.entries}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
